/**
 * Shared shadcn-style dialog panel animation (tailwindcss-animate). Corvu
 * panels animate off their `data-open`/`data-closed` presence attributes.
 * The chrome around a panel — backdrop and balance pills — belongs to the
 * overlay scene (components/overlay-scene.tsx), whose enter/exit sets
 * toggle off the scene's closing clock with matching durations.
 */

/** Fade + zoom in/out for a corvu dialog panel. */
export const dialogPanelAnimation =
  'duration-300 data-[closed]:duration-200 data-[open]:animate-in data-[closed]:animate-out data-[closed]:fade-out data-[open]:fade-in data-[closed]:zoom-out-95 data-[open]:zoom-in-95'

/** Fade in/out for a product detail's overlay, over the catalogue. */
export const detailOverlayAnimation =
  'z-30 duration-300 data-[closed]:duration-200 data-[open]:animate-in data-[closed]:animate-out data-[closed]:fade-out data-[open]:fade-in'

/**
 * The launch deep link's detail overlay (GLOSSARY.md "Shop launch deep link"):
 * a solid cover over the catalogue rather than the frame's blurred glass.
 */
export const deepLinkOverlayCover = 'bg-[#C5DAD5] backdrop-blur-none'

/**
 * Resolves once every animation running on the element or its subtree has
 * finished — a panel's enter animation included — and at once when nothing
 * is running or the platform has no Web Animations (happy-dom). A cancelled
 * animation counts as finished: the element is gone, nothing left to wait
 * for.
 */
/**
 * Resolves once a settled detail panel is presented (GLOSSARY.md "Reveal
 * surface"): every image under `imagesRoot` — the panel itself by default —
 * decoded, then every animation on the panel finished. A decode failure is a
 * broken image, not a reason to keep waiting.
 */
export function panelPresented(
  panel: Element,
  imagesRoot: Element = panel,
): Promise<void> {
  return Promise.all(
    Array.from(imagesRoot.querySelectorAll('img'), (image) =>
      image.decode().catch(() => undefined),
    ),
  ).then(() => animationsFinished(panel))
}

export function animationsFinished(element: Element): Promise<void> {
  const getAnimations = (element as Partial<Pick<Element, 'getAnimations'>>)
    .getAnimations
  const animations = getAnimations
    ? getAnimations.call(element, { subtree: true })
    : []
  return Promise.all(
    animations.map((animation) =>
      animation.finished.then(
        () => undefined,
        () => undefined,
      ),
    ),
  ).then(() => undefined)
}
